import React from 'react';
import { makeStyles } from '@mui/styles';
import Paper from "@mui/material/Paper";
import { Grid } from "@mui/material";

const useStyles = makeStyles({
  root: {
    background: "transparent",
    boxShadow: "none",
  },
  // Listing Layout Styles
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "0",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    border: "1px solid #e8e8e8",
  },
  listHeader: {
    display: "grid",
    gridTemplateColumns: "2.5fr 120px 1.5fr",
    gap: "24px",
    padding: "14px 24px",
    background: "#fafafa",
    borderBottom: "1px solid #e8e8e8",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    '@media (max-width: 768px)': {
      display: "none",
    },
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "2.5fr 120px 1.5fr",
    gap: "24px",
    padding: "16px 24px",
    alignItems: "center",
    borderBottom: "1px solid #f0f0f0",
    borderLeft: "3px solid #9e9e9e",
    background: "linear-gradient(90deg, rgba(158, 158, 158, 0.02) 0%, transparent 100%)",
    transition: "all 0.15s ease",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      background: "linear-gradient(90deg, rgba(158, 158, 158, 0.05) 0%, #fafbfc 100%)",
    },
    '@media (max-width: 768px)': {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "12px",
      padding: "16px 20px",
    },
  },
  testNameCell: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
    '@media (max-width: 768px)': {
      width: "100%",
    },
  },
  testName: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1a1a1a",
    margin: 0,
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "default",
  },
  statusCell: {
    display: "flex",
    justifyContent: "center",
    '@media (max-width: 768px)': {
      order: -1,
      justifyContent: "flex-start",
    },
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "5px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 500,
    whiteSpace: "nowrap",
    minWidth: "95px",
    background: "rgba(198, 40, 40, 0.08)",
    color: "#c62828",
  },
  statusDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#c62828",
  },
  scheduleCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "13px",
    color: "#555",
    '@media (max-width: 768px)': {
      width: "100%",
      padding: "8px 0",
      borderTop: "1px solid #f0f0f0",
      justifyContent: "flex-start",
    },
  },
  scheduleIcon: {
    width: "16px",
    height: "16px",
    color: "#888",
    flexShrink: 0,
  },
  scheduleText: {
    fontWeight: 500,
    color: "#333",
  },
  // Empty state
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 24px",
    textAlign: "center",
    background: "#fff",
    borderRadius: "12px",
    border: "1px solid #e8e8e8",
  },
  emptyIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    backgroundColor: "#f5f0f7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
  },
  emptyTitle: {
    margin: "0 0 8px 0",
    fontSize: "17px",
    fontWeight: "600",
    color: "#333",
  },
  emptyText: {
    margin: "0",
    fontSize: "14px",
    color: "#666",
    maxWidth: "300px",
    lineHeight: 1.5,
  },
});

export default function CustomizedTables(props) {
  const classes = useStyles();
  const { tableData, typeButton } = props;

  const hasData = tableData && tableData['tableData'] !== undefined;

  const formatDate = (dateString) => {
    if (!dateString || dateString === "N.A.") return "N.A.";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).replace(/ /g, "-") +
      " \u2022 " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  return (
    <Grid item xs={12}>
      <Paper className={classes.root}>
        {hasData ? (
          <div className={classes.listContainer}>
            {/* List Header */}
            <div className={classes.listHeader}>
              <span>Name</span>
              <span style={{ textAlign: "center" }}>Status</span>
              <span style={{ textAlign: "center" }}>Scheduled</span>
            </div>

            {tableData.tableData.map((row, index) => (
              <div key={`expired-${index}`} className={classes.listItem}>
                <div className={classes.testNameCell}>
                  <h3 className={classes.testName} title={row.name}>{row.name}</h3>
                </div>

                <div className={classes.statusCell}>
                  <span className={classes.statusBadge}>
                    <span className={classes.statusDot}></span>
                    {row.sts === '112' ? 'System Aborted' : 'Expired'}
                  </span>
                </div>

                <div className={classes.scheduleCell}>
                  <svg className={classes.scheduleIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <span className={classes.scheduleText}>{formatDate(row.date)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={classes.emptyState}>
            <div className={classes.emptyIcon}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7030a0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <h4 className={classes.emptyTitle}>
              {typeButton === 'FeedbackExpired' ? 'No Expired Feedback' : typeButton === 'SurveyExpired' ? 'No Expired Survey' : typeButton === 'WorkFlowExpired' ? 'No Expired WorkFlow' : 'No Expired Tests'}
            </h4>
            <p className={classes.emptyText}>
              No expired items to display at this time. In-progress tests are system aborted on expiry.
            </p>
          </div>
        )}
      </Paper>
    </Grid>
  );
}
